import React from "react";
import { Helmet } from 'react-helmet-async';
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, HelpCircle, ChevronRight, ChevronLeft } from "lucide-react";
import { VisaGuideData } from "@/types/visa";
import VisaSelector from "@/components/visa-guide/VisaSelector";
import VisaOverview from "@/components/visa-guide/VisaOverview";
import VisaRequirements from "@/components/visa-guide/VisaRequirements";
import VisaProcess from "@/components/visa-guide/VisaProcess";
import VisaResources from "@/components/visa-guide/VisaResources";
import { visaGuideData } from "@/data/visaGuideData";

// Updated blog posts array with most recent first
const blogPosts = [
  {
    id: "common-mistakes",
    title: "Common Mistakes International Applicants Make When Seeking Sponsorship",
    description: "Learn about the common mistakes international applicants make when seeking visa sponsorship and how to avoid them.",
    image: "/lovable-uploads/38300a84-9f3d-405b-b1a1-9ae41d0e6f15.png",
    date: "May 2, 2025",
    readTime: "12 min read"
  },
  {
    id: "employer-sponsorship",
    title: "What Employers Look for When Sponsoring a Work Visa",
    description: "Understand the key factors that influence employers' decisions to sponsor foreign workers for visas.",
    image: "/lovable-uploads/280595a6-6bf9-4a18-a381-50f89cbd24ba.png",
    date: "April 30, 2025",
    readTime: "14 min read"
  },
  {
    id: "non-tech-visa-jobs",
    title: "Visa-Sponsored Jobs for Non-Tech Professionals in 2025",
    description: "Explore diverse opportunities for non-technical professionals seeking international employment with visa sponsorship.",
    image: "/lovable-uploads/9c9d1274-143d-4912-9cb6-964cc86d2cc7.png",
    date: "April 29, 2025",
    readTime: "13 min read"
  }
];

const VisaTravelGuidesPage = () => {
  const navigate = useNavigate();
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedVisaType, setSelectedVisaType] = useState<string>("");
  const [travelingFrom, setTravelingFrom] = useState<string>("");
  const [activeTab, setActiveTab] = useState("overview");
  const [animateIn, setAnimateIn] = useState(false);
  const [expandedRequirements, setExpandedRequirements] = useState<number[]>([]);
  const [expandedSteps, setExpandedSteps] = useState<number[]>([]);

  const countries = [
    { value: "usa", label: "United States 🇺🇸" },
    { value: "uk", label: "United Kingdom 🇬🇧" },
    { value: "canada", label: "Canada 🇨🇦" },
    { value: "australia", label: "Australia 🇦🇺" },
    { value: "germany", label: "Germany 🇩🇪" },
    { value: "ireland", label: "Ireland 🇮🇪" },
    { value: "netherlands", label: "Netherlands 🇳🇱" },
    { value: "spain", label: "Spain 🇪🇸" },
    { value: "italy", label: "Italy 🇮🇹" },
    { value: "japan", label: "Japan 🇯🇵" },
    { value: "singapore", label: "Singapore 🇸🇬" }
  ];

  const originCountries = [
    { value: "usa", label: "United States 🇺🇸" },
    { value: "uk", label: "United Kingdom 🇬🇧" },
    { value: "canada", label: "Canada 🇨🇦" },
    { value: "australia", label: "Australia 🇦🇺" },
    { value: "germany", label: "Germany 🇩🇪" },
    { value: "india", label: "India 🇮🇳" },
    { value: "japan", label: "Japan 🇯🇵" },
    { value: "china", label: "China 🇨🇳" },
    { value: "brazil", label: "Brazil 🇧🇷" },
    { value: "nigeria", label: "Nigeria 🇳🇬" },
  ];

  const getVisaTypesForCountry = (country: string) => {
    const commonTypes = [
      { value: "tourist", label: "Tourist/Visitor Visa" },
      { value: "student", label: "Student Visa" },
      { value: "work", label: "Work Visa" },
      { value: "business", label: "Business/Investor Visa" },
      { value: "family", label: "Family/Partner Visa" },
      { value: "skilled", label: "Skilled Worker/Talent Visa" },
      { value: "startup", label: "Startup/Entrepreneur Visa" },
      { value: "other", label: "Other Visa Types" }
    ];

    switch(country) {
      case 'usa':
        return commonTypes.map(type => {
          const labels: { [key: string]: string } = {
            "tourist": "B-1/B-2 Visitor Visa",
            "student": "F-1/M-1 Student Visa",
            "work": "H-1B Work Visa",
            "business": "E-1/E-2 Business Visa",
            "family": "K-1/IR/CR Family Visa",
            "skilled": "O-1 Extraordinary Ability",
            "startup": "E-2 Treaty Investor",
            "other": "Other US Visa Types"
          };
          return { ...type, label: labels[type.value] || type.label };
        });
      case 'uk':
        return commonTypes.map(type => {
          const labels: { [key: string]: string } = {
            "tourist": "Standard Visitor Visa",
            "student": "Student Visa (formerly Tier 4)",
            "work": "Skilled Worker Visa",
            "business": "Innovator Visa",
            "family": "Family Visa",
            "skilled": "Global Talent Visa",
            "startup": "Start-up Visa",
            "other": "Other UK Visa Types"
          };
          return { ...type, label: labels[type.value] || type.label };
        });
      default:
        return commonTypes;
    }
  };

  const visaTypes = getVisaTypesForCountry(selectedCountry);

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setSelectedVisaType("");
    setAnimateIn(false);
    setTimeout(() => {
      setAnimateIn(true);
    }, 300);
  };

  const handleVisaTypeChange = (value: string) => {
    setSelectedVisaType(value);
    setAnimateIn(false);
    setTimeout(() => {
      setAnimateIn(true);
    }, 300);
  };

  const handleTravelingFromChange = (value: string) => {
    setTravelingFrom(value);
    setAnimateIn(false);
    setTimeout(() => {
      setAnimateIn(true);
    }, 300);
  };

  const toggleRequirement = (index: number) => {
    setExpandedRequirements(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const toggleStep = (index: number) => {
    setExpandedSteps(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

  const navigateTab = (direction: 'next' | 'prev') => {
    const tabs = ["overview", "requirements", "process", "resources"];
    const currentIndex = tabs.indexOf(activeTab);
    
    if (direction === 'next' && currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    } else if (direction === 'prev' && currentIndex > 0) {
      setActiveTab(tabs[currentIndex - 1]);
    }
  };

  const renderContent = () => {
    if (!selectedCountry) {
      return (
        <div className="text-center py-12">
          <HelpCircle className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Select a Country</h3>
          <p className="text-gray-500">Choose a destination country to view visa requirements.</p>
        </div>
      );
    }
    
    if (!selectedVisaType) {
      return (
        <div className="text-center py-12">
          <HelpCircle className="mx-auto h-16 w-16 text-gray-300 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Select a Visa Type</h3>
          <p className="text-gray-500">Choose a visa type to view specific requirements and application process.</p>
        </div>
      );
    }
    
    const countryData = visaGuideData[selectedCountry];
    if (!countryData) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-16 w-16 text-amber-500 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Information Not Available</h3>
          <p className="text-gray-500">We don't have information for this country yet.</p>
        </div>
      );
    }
    
    const visaData = countryData[selectedVisaType];
    if (!visaData) {
      return (
        <div className="text-center py-12">
          <AlertCircle className="mx-auto h-16 w-16 text-amber-500 mb-4" />
          <h3 className="text-xl font-medium text-gray-700 mb-2">Information Not Available</h3>
          <p className="text-gray-500">We don't have information for this visa type yet.</p>
        </div>
      );
    }
    
    const originData = travelingFrom && visaData.originSpecific && visaData.originSpecific[travelingFrom];
    
    return (
      <div className={`transition-opacity duration-300 ${animateIn ? 'opacity-100' : 'opacity-0'}`}>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid grid-cols-4 mb-8">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="requirements">Requirements</TabsTrigger>
            <TabsTrigger value="process">Process</TabsTrigger>
            <TabsTrigger value="resources">Resources</TabsTrigger>
          </TabsList>
          
          <TabsContent value="overview">
            <VisaOverview
              visaData={visaData}
              originData={originData}
              originCountry={travelingFrom}
              originCountries={originCountries}
            />
          </TabsContent>
          
          <TabsContent value="requirements">
            <VisaRequirements
              visaData={visaData}
              originData={originData}
              originCountries={originCountries}
              travelingFrom={travelingFrom}
              expandedRequirements={expandedRequirements}
              onToggleRequirement={toggleRequirement}
            />
          </TabsContent>
          
          <TabsContent value="process">
            <VisaProcess
              visaData={visaData}
              expandedSteps={expandedSteps}
              onToggleStep={toggleStep}
            />
          </TabsContent>
          
          <TabsContent value="resources">
            <VisaResources visaData={visaData} />
          </TabsContent>
        </Tabs>
        
        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={() => navigateTab('prev')}
            disabled={activeTab === "overview"}
            className="flex items-center"
          >
            <ChevronLeft className="mr-2 h-4 w-4" />
            Previous
          </Button>
          <Button
            onClick={() => navigateTab('next')}
            disabled={activeTab === "resources"}
            className="flex items-center"
          >
            Next
            <ChevronRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Visanow - Visa Travel Guides</title>
      </Helmet>
      <Navbar />
      
      <div className="container mx-auto py-8 md:py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3 md:mb-4">Visa Travel Guides</h1>
          <p className="text-lg md:text-xl text-gray-600 mb-8 md:mb-12 mx-auto max-w-2xl">
            Comprehensive guides to help you navigate the visa application process for your destination.
          </p>
          
          <VisaSelector
            selectedCountry={selectedCountry}
            selectedVisaType={selectedVisaType}
            travelingFrom={travelingFrom}
            onCountryChange={handleCountryChange}
            onVisaTypeChange={handleVisaTypeChange}
            onTravelingFromChange={handleTravelingFromChange}
            countries={countries}
            visaTypes={visaTypes}
            originCountries={originCountries}
          />
          
          {/* Google AdSense Ad - Visa Travel Guides */}
          <div className="my-8 text-center">
            <ins className="adsbygoogle"
                style={{display: "block"}}
                data-ad-client="ca-pub-0483659727992282"
                data-ad-slot="4344254861"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>
            <script dangerouslySetInnerHTML={{__html: `
                (adsbygoogle = window.adsbygoogle || []).push({});
            `}} />
          </div>
          
          {renderContent()}

          <div className="mt-12 md:mt-16">
            <h2 className="text-2xl md:text-3xl font-bold mb-6 md:mb-8 text-center">Get smarter, with our blog.</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {blogPosts.map((post) => (
                <Link to={`/blog/${post.id}`} key={post.id} className="bg-white rounded-xl overflow-hidden shadow-sm block transition-transform duration-300 hover:-translate-y-1">
                  <img src={post.image} alt={post.title} className="w-full h-48 object-cover" />
                  <div className="p-4 md:p-6">
                    <div className="text-xs text-purple-600 font-semibold mb-2">{post.date}</div>
                    <h3 className="font-bold text-base md:text-lg mb-2">{post.title}</h3>
                    <p className="text-sm text-gray-600 line-clamp-3 mb-3">{post.description}</p>
                    <div className="text-sm text-gray-500">{post.readTime}</div>
                    <div className="mt-3 text-purple-600">Read article →</div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="text-center mt-6 md:mt-8">
              <Link to="/blog">
                <button className="border border-gray-300 px-4 md:px-6 py-2 rounded-md text-gray-800 font-medium hover:bg-gray-50 transition-colors">
                  See all articles
                </button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default VisaTravelGuidesPage;
